import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import qs from 'qs';
import moment from 'moment';

import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import intl from '../../shared/intl';

import createCalendar from '../../shared/calendar';
import createFilters from '../../shared/filters';
import filtersInitialState from '../shared/filter-config';

import {
	getRequestQuery,
	mapQueryFilters,
	getCurrentCurrency,
	toggleInitialCurrency,
	initCurrencies,
	initDefaultCurrenciesAction
} from '../shared/utils';
import { REFETCH_ALL_DATA } from '../../root';

import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import omit from 'lodash/omit';
import _get from 'lodash/get';
import forEach from 'lodash/forEach';
import orderBy from 'lodash/orderBy';
import find from 'lodash/find';

import { APPLY_MASTER_SWITCH } from '../general';

const isRocket = process.env.CPA_ENV === 'rocket';

const calendar = createCalendar({
	namespace: 'analytics',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'analytics',
	stateNodeName: 'filters',
	filtersInitialState: [].concat(filtersInitialState, [{
		title: 'Сплит',
		suggest: [{
			placeholder: 'Выберите сплит-сессию',
			key: 'session_ids',
			options: {
				source: endpoints.STATISTICS.FILTERS.SPLIT_SESSIONS,
				type: 'fromServer'
			}
		}]
	},
	{
		title: 'Статус',
		suggest: [{
			placeholder: 'Выберите статус',
			key: 'statuses',
			options: {
				source: 'app.lists.conversion-statuses',
				type: 'fromState'
			}
		}]
	}])
});

const FETCH_DATA_SUCCESS = 'analytics/fetch-data-success';
const FETCH_DATA_ERROR = 'analytics/fetch-data-error';

const STATS_RESET = 'analytics/reset';
const STATS_APPLY_CALENDAR_VALUE = 'analytics/apply-calendar-value';
const STATS_UPDATE_QUERY = 'analytics/update-query';

const SHOW_PROGRESS_BAR = 'analytics/show-progress-bar';

const CHANGE_CURRENCY = 'analytics/change-currency';

const GET_LAST_CONVERSION_CURRENCY_SUCCESS = 'statistics/get-last-conversion-success';
const GET_LAST_CONVERSION_CURRENCY_ERROR = 'statistics/get-last-conversion-error';
const INIT_CURRENCIES = 'statistics/init-currencies';

const initialState = {
	data: null,
	query: {
		currency: null,
		groups: []
	},
	calendar: calendar.initialState,
	filters: filters.initialState,
	fullAppliedFilters: null,
	listOfCurrencies: [],
	showProgressBar: false,
	error: false
};

export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			initDefaultCurrenciesAction(getState, dispatch, INIT_CURRENCIES, 'analytics');
			const masterSwitchActiveTab = selectors.getMasterSwitchActiveTab(getState());
			const request = http({
				url: `${endpoints.STATISTICS.GET_LAST_CONVERSION}?owner_type=${masterSwitchActiveTab}`,
				method: 'get',
				actions: [
					GET_LAST_CONVERSION_CURRENCY_SUCCESS,
					GET_LAST_CONVERSION_CURRENCY_ERROR
				]
			});
			request(dispatch).then(() => fetchRequest(dispatch, getState, props));
		},

		statsApply: () => (dispatch, getState) => {
			dispatch({
				type: SHOW_PROGRESS_BAR
			});

			dispatch(actions.updateQuery());

			fetchRequest(dispatch, getState);
		},

		statsReset: () => dispatch => {
			dispatch(replace('statistics/analytics/'));
			dispatch({ type: STATS_RESET });
		},

		applyCalendarValue: calendarValue => ({
			type: STATS_APPLY_CALENDAR_VALUE,
			payload: calendarValue
		}),

		statisticsFilterApply: filterId => (dispatch, getState) => {
			filters.actions.filterApply(filterId)(dispatch, getState);
		},

		updateQuery: () => ({
			type: STATS_UPDATE_QUERY
		}),

		resetUnappliedFilters: () => (dispatch, getState) => {
			let currentQuery = getState().analytics.query;
			let fullAppliedFilters = getState().analytics.fullAppliedFilters;

			dispatch(
				filters.actions.filterApplyByExternalQuery({
					...currentQuery,
					filters: fullAppliedFilters
				})
			);
			currentQuery && currentQuery.date_range && dispatch(actions.applyCalendarValue(currentQuery.date_range));
		},

		showProgressBar: () => ({
			type: SHOW_PROGRESS_BAR
		}),

		changeCurrency: currencyId => dispatch => {
			dispatch({
				type: CHANGE_CURRENCY,
				payload: currencyId
			});

			dispatch(actions.statsApply());
		},

		applyMasterSwitch: id => dispatch => {
			http({
				url: `${endpoints.STATISTICS.GET_LAST_CONVERSION}?owner_type=${id}`,
				method: 'get',
				actions: [
					GET_LAST_CONVERSION_CURRENCY_SUCCESS,
					GET_LAST_CONVERSION_CURRENCY_ERROR
				]
			})(dispatch).then(() => {
				return dispatch({ type: APPLY_MASTER_SWITCH, payload: id });
			});
		}
	},
	calendar.actions,
	filters.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			const shouldSaveFiltersState =
				action.payload.pathname.indexOf('statistics/analytics') !== -1 ||
				action.payload.pathname.indexOf('statistics/general') !== -1 ||
				action.payload.pathname.indexOf('statistics/conversions') !== -1;
			return shouldSaveFiltersState ? { ...state, data: null } : initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			let data = action.payload.data;
			const fieldsToProcess = data[0] && omit(Object.keys(data[0]), ['country']) || [];
			const preparedData = reduce(data, (res, row, index) => {
				forEach(fieldsToProcess, f => {
					res[f] = (res[f] || 0) + Number(row[f]);
					if (!isRocket) {
						res.cnt_ages = (res.cnt_ages || 0)
							+ Number(row.age_0_19) + Number(row.age_20_24) + Number(row.age_25_31) + Number(row.age_32_36)
							+ Number(row.age_37_39) + Number(row.age_40_44) + Number(row.age_45_51) + Number(row.age_52_54)
							+ Number(row.age_55_59) + Number(row.age_60_64) + Number(row.age_65_69) + Number(row.age_70_74)
							+ Number(row.age_75_81) + Number(row.age_82);
					} else {
						res.cnt_ages = (res.cnt_ages || 0)
							+ Number(row.age_0_25) + Number(row.age_26_35) + Number(row.age_36_45) + Number(row.age_46_55)
							+ Number(row.age_56_65) + Number(row.age_66_75) + Number(row.age_76);
					}
				});
				res.showEmptyAgeChart = !res.cnt_ages;
				if (!row.country) {
					row.country = 'Unknown';
				}
				const country = row.country.replace(/\(.*\)/, '').trim();
				res.countries.push({ country, cnt: parseInt(row.total_conversions) });
				return res;
			}, {countries: [], showEmptyAgeChart: true});
			preparedData.countries = orderBy(preparedData.countries, ['cnt'], ['desc']);
			return {
				...state,
				data: [preparedData],
				showProgressBar: false
			};
		},
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, data: null, error: true, showProgressBar: false }),
		[GET_LAST_CONVERSION_CURRENCY_ERROR]: (state, action) =>
			({ ...state, data: null, error: true, showProgressBar: false }),
		[STATS_APPLY_CALENDAR_VALUE]: (state, action) => {
			const dateRange = action.payload;
			const calendar = {
				...state.calendar,
				value: {
					start: (dateRange.start && moment(dateRange.start).format('YYYY-MM-DD')) || state.calendar.value.start,
					end: (dateRange.end && moment(dateRange.end).format('YYYY-MM-DD')) || state.calendar.value.end
				}
			};

			return { ...state, calendar };
		},

		[STATS_UPDATE_QUERY]: state => {
			const filters = state.filters && mapQueryFilters(state.filters);
			const fullFilters = state.filters && mapQueryFilters(state.filters, false);
			const date_range = state.calendar && state.calendar.value;

			return {
				...state,
				query: {
					...state.query,
					filters: filters || state.query.filters,
					date_range: date_range || state.query.date_range
				},
				fullAppliedFilters: fullFilters
			};
		},

		[STATS_RESET]: () => initialState,
		[REFETCH_ALL_DATA]: () => initialState,
		[SHOW_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
		[CHANGE_CURRENCY]: (state, action) => ({
			...state,
			query: {
				...state.query,
				currency: action.payload
			}
		}),
		[APPLY_MASTER_SWITCH]: (state, action) => ({
			...state,
			data: null
		}),
		[GET_LAST_CONVERSION_CURRENCY_SUCCESS]: (state, action) => isRocket ? state : toggleInitialCurrency(state, action),
		[INIT_CURRENCIES]: (state, action) => initCurrencies(state, action)
	},
	calendar.reducers,
	filters.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

export const selectors = {
	getData: state => state.analytics.data,
	getAffiliateData: state => state.app.userData && state.app.userData.general,
	getDataTable: state => {
		const tableRows = state.analytics.data;

		return {
			tableRows,
			currency: state.app.userData && state.app.userData.activeBalance
		};
	},
	getCalendar: state => calendar.selectors.getCalendar(state => state.analytics.calendar, state => state)(state),
	getFilters: state => filters.selectors.selectFilters(state => state.analytics.filters, state => state)(state),
	getAppliedFilters: state => filters.selectors.appliedFilters(state),
	getIntl: intl,
	getProgressBar: state => state.analytics.showProgressBar,
	getIsNewRegisteredUser: state => _get(state, 'app.userData.general.level') === 'registered',
	getListOfCurrencies: state => state.app.userData.balances.map(item => ({
		id: item.currency,
		text: item.currency
	})),
	getActiveCurrency: state => getCurrentCurrency(state, 'analytics'),
	getMasterSwitchConfig: state => state.statistics.masterSwitchConfig,
	getMasterSwitchActiveTab: state => state.statistics.masterSwitchConfig.filter(i => i.active)[0].id,
	getIsMasterStatus: state => state.app.userData.general.is_master,
	getError: state => state.analytics.error
};

function fetchRequest(dispatch, getState, props) {
	const state = getState();
	const user = state.app.userData.general;
	const activeBalance = getCurrentCurrency(state, 'analytics');
	const externalQuery = (props && props.location && props.location.query && qs.parse(props.location.query)) || {};
	const query = qs.stringify(
		getRequestQuery(
			state.analytics,
			activeBalance,
			externalQuery,
			false,
			user.is_master,
			state.statistics.masterSwitchConfig
		)
	);
	const activeMasterSwitchSetting = find(state.statistics.masterSwitchConfig, { active: true }).id;

	const request = http({
		url: `${endpoints.STATISTICS.ANALYTICS}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		addToResponse: externalQuery
	});

	request(dispatch).then(action => {
		if (action.type === FETCH_DATA_SUCCESS) {
			if (user.is_master && activeMasterSwitchSetting !== 'master_only') {
				dispatch(filters.actions.filterReplaceState(
					[].concat(filtersInitialState, {
						title: 'Вебмастера',
						suggest: [{
							placeholder: 'Выберите вебмастера',
							key: 'affiliates',
							options: {
								source: endpoints.STATISTICS.FILTERS.AFFILIATES,
								type: 'fromServer'
							}
						}]
					})
				));
			}
			if (!isEmpty(externalQuery)) {
				dispatch(filters.actions.filterApplyByExternalQuery(externalQuery));
				if (!isEmpty(externalQuery.date_range)) {
					dispatch(actions.applyCalendarValue(externalQuery.date_range));
				}
				dispatch(actions.updateQuery());
			} else {
				dispatch(actions.resetUnappliedFilters());
			}
		}
	});
}



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/statistics/analytics/index.js